import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { FaBook, FaSignOutAlt, FaUserCircle } from 'react-icons/fa';

const Header = () => {
  const { currentUser, userRole, isAuthenticated, logoutUser } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isProfileOpen) setIsProfileOpen(false);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const toggleProfile = () => {
    setIsProfileOpen(!isProfileOpen);
    if (isMenuOpen) setIsMenuOpen(false);
  };

  return (
    <header className="bg-white/95 backdrop-blur-sm shadow-lg border-b border-gray-100 fixed w-full top-0 z-50">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <Link to="/" className="flex items-center group">
          <div className="bg-primary/10 p-2 rounded-xl group-hover:bg-primary/20 transition-all duration-300 mr-3">
            <FaBook className="text-primary text-xl" />
          </div>
          <span className="text-2xl font-bold text-gray-900 group-hover:text-primary transition-colors duration-300">Page4Change</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <Link to="/" className="text-gray-700 hover:text-primary font-medium transition-all duration-300 relative group">
            <span>Home</span>
            <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></div>
          </Link>
          <Link to="/about" className="text-gray-700 hover:text-primary font-medium transition-all duration-300 relative group">
            <span>About</span>
            <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></div>
          </Link>
          
          {isAuthenticated ? (
            <>
              <Link to="/books" className="text-gray-700 hover:text-primary font-medium transition-all duration-300 relative group">
                <span>Books</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></div>
              </Link>
              <Link to="/dashboard" className="text-gray-700 hover:text-primary font-medium transition-all duration-300 relative group">
                <span>Dashboard</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></div>
              </Link>
              
              {userRole === 'admin' && (
                <Link to="/admin" className="text-gray-700 hover:text-primary font-medium transition-all duration-300 relative group">
                  <span>Admin</span>
                  <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></div>
                </Link>
              )}
              
              {userRole === 'teacher' && (
                <Link to="/teacher/dashboard" className="text-gray-700 hover:text-primary font-medium transition-all duration-300 relative group">
                  <span>Teacher</span>
                  <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></div>
                </Link>
              )}
              
              <div className="relative">
                <button 
                  onClick={toggleProfile}
                  className="flex items-center text-gray-700 hover:text-primary transition-all duration-300 bg-gray-50 hover:bg-primary/10 rounded-full px-4 py-2 font-medium"
                >
                  <FaUserCircle className="mr-2 text-lg" />
                  <span>{currentUser?.displayName || 'Account'}</span>
                </button>
                
                <AnimatePresence>
                  {isProfileOpen && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="absolute right-0 mt-3 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-10 overflow-hidden"
                    >
                      <div className="px-4 py-3 text-sm text-gray-600 border-b border-gray-100 bg-gray-50/50">
                        <div className="font-medium text-gray-900">{currentUser?.displayName || 'User'}</div>
                        <div className="text-gray-500">{currentUser?.email}</div>
                      </div>
                      <div className="px-4 py-2 text-sm text-gray-600 border-b border-gray-100">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize">
                          {userRole}
                        </span>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 flex items-center transition-colors duration-200"
                      >
                        <FaSignOutAlt className="mr-3 text-red-500" /> Sign out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="text-gray-700 hover:text-primary font-medium transition-all duration-300 relative group">
                <span>Login</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-300"></div>
              </Link>
              <Link to="/register" className="bg-gradient-to-r from-primary to-primary-dark text-white px-6 py-2.5 rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300 font-medium">
                Register
              </Link>
            </>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <button 
          onClick={toggleMenu}
          className="md:hidden text-gray-700 hover:text-primary focus:outline-none p-2 hover:bg-gray-100 rounded-lg transition-all duration-300"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {isMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden bg-white/95 backdrop-blur-sm border-t border-gray-100"
          >
            <div className="px-4 py-4 space-y-2">
              <Link to="/" onClick={closeMenu} className="block py-3 px-4 text-gray-700 hover:text-primary hover:bg-primary/5 rounded-xl transition-all duration-300 font-medium">
                Home
              </Link>
              <Link to="/about" onClick={closeMenu} className="block py-3 px-4 text-gray-700 hover:text-primary hover:bg-primary/5 rounded-xl transition-all duration-300 font-medium">
                About
              </Link>
              
              {isAuthenticated ? (
                <>
                  <Link to="/books" onClick={closeMenu} className="block py-3 px-4 text-gray-700 hover:text-primary hover:bg-primary/5 rounded-xl transition-all duration-300 font-medium">
                    Books
                  </Link>
                  <Link to="/dashboard" onClick={closeMenu} className="block py-3 px-4 text-gray-700 hover:text-primary hover:bg-primary/5 rounded-xl transition-all duration-300 font-medium">
                    Dashboard
                  </Link>
                  
                  {userRole === 'admin' && (
                    <Link to="/admin" onClick={closeMenu} className="block py-3 px-4 text-gray-700 hover:text-primary hover:bg-primary/5 rounded-xl transition-all duration-300 font-medium">
                      Admin
                    </Link>
                  )}
                  
                  {userRole === 'teacher' && (
                    <Link to="/teacher/dashboard" onClick={closeMenu} className="block py-3 px-4 text-gray-700 hover:text-primary hover:bg-primary/5 rounded-xl transition-all duration-300 font-medium">
                      Teacher
                    </Link>
                  )}
                  
                  <div className="py-3 px-4 border-t border-gray-100 mt-2">
                    <div className="bg-gray-50 rounded-xl p-3 mb-3">
                      <p className="text-sm font-medium text-gray-900">{currentUser?.displayName || 'User'}</p>
                      <p className="text-sm text-gray-500">{currentUser?.email}</p>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize mt-2">
                        {userRole}
                      </span>
                    </div>
                    <button 
                      onClick={handleLogout}
                      className="w-full flex items-center py-3 px-4 text-red-600 hover:bg-red-50 rounded-xl transition-all duration-300 font-medium"
                    >
                      <FaSignOutAlt className="mr-3" /> Sign out
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <Link to="/login" className="block py-3 px-4 text-gray-700 hover:text-primary hover:bg-primary/5 rounded-xl transition-all duration-300 font-medium">
                    Login
                  </Link>
                  <Link to="/register" className="block py-3 px-4 text-primary hover:bg-primary hover:text-white rounded-xl transition-all duration-300 font-medium">
                    Register
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
